import { calculateOffset } from 'shared/services/ContentService'
import { createSelector } from 'reselect'
import { RenderProperty } from 'shared/renderProperties'
import { numberRenderPropertySelector } from '~/app/store/config/reducer'
import { DEFAULT_PAGING_SIZES, INITIAL_STATE, MINIMUM_TEMPLATES_SIZE } from '~/app/store/paging/constants'
import { shouldHideCareTileSelector, shouldHideFbuTileSelector } from '~/app/store/refinement/selectors'

/**
 * Selects the current `pageSize` from the state
 * @param state
 */
export const currentPageSizeSelector = (state: State.GlobalState): number => state.paging.pageSize

/**
 * getCurrentOffsetModifier
 * Returns the count of special tiles (FBU + DIFY) rendered BEFORE the current lazy batch
 * on page 1. (For page > 1, this value is not used.)
 *
 * Examples when both tiles visible (FBU=1, DIFY=1):
 *  loadedPosition=0 -> 0 (nothing before first batch)
 *  loadedPosition=1 -> 2 (FBU + DIFY from batch 0)
 *  loadedPosition=2 -> 3 (FBU + DIFY from batch 0, plus DIFY from batch 1)
 *  loadedPosition=3 -> 4 (FBU + DIFY from batch 0, plus DIFY from batches 1 and 2)
 */
const specialsPerBatchSelector = createSelector(shouldHideFbuTileSelector, shouldHideCareTileSelector, (hideFBU, hideDIFY) => {
  const fbu = hideFBU ? 0 : 1
  const dify = hideDIFY ? 0 : 1

  // Offset modifier that appear in each page-1 batch:
  // loadedPosition=0: FBU + DIFY (if visible)
  // loadedPosition=1..3: DIFY only (if visible)
  return [fbu + dify, dify, dify, dify]
})

export const getCurrentOffsetModifier = createSelector(
  [specialsPerBatchSelector, (_: State.GlobalState, loadedPosition = 0): number => loadedPosition],
  (specialsPerBatch, loadedPosition) => specialsPerBatch.slice(0, loadedPosition).reduce((a, b) => a + b, 0),
)

/** First-page batch sizes after accounting for specials. */
const firstPageBatchSizesSelector = createSelector([specialsPerBatchSelector], (specialsPerBatch): number[] => {
  const size0 = MINIMUM_TEMPLATES_SIZE - specialsPerBatch[0] // loadedPosition=0
  const sizeN = MINIMUM_TEMPLATES_SIZE - specialsPerBatch[1] // loadedPosition=1..3

  return [size0, sizeN, sizeN, sizeN]
})

/** Cumulative template count produced by all 4 first-page batches (length 5, starts with 0). */
const firstPageCumulativeSelector = createSelector([firstPageBatchSizesSelector], (sizes) =>
  sizes.reduce((acc, size) => [...acc, acc[acc.length - 1] + size], [0]),
)

// Given visible page size (24 | 48 | 96), how many 24-slot batches fit on page 1?
const batchesNeededOnFirstPageSelector = createSelector([currentPageSizeSelector], (visiblePageSize) => {
  // Each lazy batch = 24 visible slots (templates + specials).
  // 24 -> 1 batch, 48 -> 2 batches, 96 -> 4 batches.
  // Clamp to [1..4] to be safe.
  const batches = Math.ceil(visiblePageSize / MINIMUM_TEMPLATES_SIZE)

  return Math.max(1, Math.min(4, batches))
})

/**
 * Total templates produced on page 1 up to the chosen visible size.
 * Pick the cumulative after `batchesNeeded` first-page batches.
 *
 * Examples (FBU+DIFY visible, MINIMUM_TEMPLATES_SIZE=24):
 *  visible=24  -> batches=1 -> cum[1] = 22
 *  visible=48  -> batches=2 -> cum[2] = 45
 *  visible=96  -> batches=4 -> cum[4] = 91
 */
const firstPageVisibleTotalSelector = createSelector(
  [firstPageCumulativeSelector, batchesNeededOnFirstPageSelector],
  (cum, batchesNeeded) => cum[batchesNeeded],
)

export const currentPageInfoSelector = createSelector(
  [
    (state: State.GlobalState): State.PagingState => state.paging,
    getCurrentOffsetModifier,
    firstPageBatchSizesSelector,
    firstPageVisibleTotalSelector,
    currentPageSizeSelector,
    (_: State.GlobalState, loadedPosition = 0): number => loadedPosition,
  ],
  (paging, offsetModifier, firstPageBatchSizes, firstPageVisibleTotal, visiblePageSize, loadedPosition) => {
    const { page } = paging
    const isFirstPage = page === 1

    if (isFirstPage) {
      // Correct offset/limit per lazy batch
      // offset = loadedPosition * 24 - offsetModifier
      // sizes (both tiles visible): [22, 23, 23, 23]
      const offset = loadedPosition * MINIMUM_TEMPLATES_SIZE - offsetModifier
      const pageSize = firstPageBatchSizes[loadedPosition]

      return { page, pageSize, offset }
    }

    // Page > 1 starts AFTER everything shown on page 1 (no FBU and DIFY afterwards)
    // With both specials tiles visible for page 1: 91
    // (page - 2) represents the number of full "regular" pages between page 1 and the current page.
    const purePagesBefore = page - 2
    // + loadedPosition * MINIMUM_TEMPLATES_SIZE (24): advance by lazy batches within the current page.
    const offset = firstPageVisibleTotal + purePagesBefore * visiblePageSize + loadedPosition * MINIMUM_TEMPLATES_SIZE
    const pageSize = MINIMUM_TEMPLATES_SIZE

    return { page, pageSize, offset }
  },
)

export const totalEntityCountSelector = (state: State.GlobalState): number => state.paging.totalEntities

/**
 * Returns the current offset based on the page and pageSize state
 * @param state
 */
export const currentOffsetSelector = (state: State.GlobalState): number => calculateOffset(state.paging.page, state.paging.pageSize)

/**
 * Selects the current `pageSize` from the state if it is different than the initialState `pagesize`, otherwise null
 * @param state
 */
export const nonDefaultPageSizeSelector = (state: State.GlobalState): number | null => {
  const configuredPageSize = numberRenderPropertySelector(state, { renderPropertyName: RenderProperty.DefaultPageSize })

  if (configuredPageSize) {
    return state.paging.pageSize === configuredPageSize ? null : state.paging.pageSize
  }
  return state.paging.pageSize === INITIAL_STATE.pageSize ? null : state.paging.pageSize
}

/**
 * Selects the current `page` from the state
 * @param state
 */
export const currentPageSelector = (state: State.GlobalState): number => state.paging.page

/**
 * Selects the current `page` from the state if it is different than the initialState `page`, otherwise null
 * @param state
 */
export const nonDefaultPageSelector = (state: State.GlobalState): number | null => (state.paging.page === INITIAL_STATE.page ? null : state.paging.page)

/**
 * Selects the total number of pages available, based on the total number
 * of items available to page including FBU and potentially DIFY tiles and the currently set page size
 * @param state
 */
export const numberOfPagesSelector = createSelector(
  (state: State.GlobalState) => state.paging.totalEntities,
  (state: State.GlobalState) => state.paging.pageSize,
  firstPageVisibleTotalSelector,
  (totalEntities: number, pageSize: number, firstPageVisibleTotal: number) => {
    if (totalEntities === 0) {
      return 0
    }

    const remaining = Math.max(0, totalEntities - firstPageVisibleTotal)

    if (remaining === 0) {
      return 1
    }
    return 1 + Math.ceil(remaining / pageSize)
  },
)

/**
 * Using `reselect`, creates a selector that returns a list of available
 * `pageSize`s. Only `pageSizes` up to the the minimal maximum `pageSize`
 * is returned. For example, if there are only 30 entities, then we only
 * need to return sizes [24, 48] since 96 wouldn't show anything more
 * than 48.
 */
export const pageSizeOptionsSelector = createSelector(
  (state: State.GlobalState) => state.paging.totalEntities,
  (totalEntities: number) => {
    const pageSizes: number[] = []

    DEFAULT_PAGING_SIZES.some((size: number) => {
      pageSizes.push(size)

      return size > totalEntities
    })

    return pageSizes
  },
)

export const loadMoreCountSelector = (state: State.GlobalState): number => state.paging.loadMoreCount
export const hasMoreTemplatesSelector = (state: State.GlobalState): boolean => state.paging.hasMoreTemplates
